/**
 * DesignSystemProvider — React Context that holds the active primitives registry.
 *
 * Usage:
 *   <DesignSystemProvider designSystem="tailwind">
 *     <AIAssistant ... />
 *   </DesignSystemProvider>
 *
 * Tailwind v4 is the only supported design system in this path.
 *
 * @module DesignSystemProvider
 */
import React, { createContext, useContext, useMemo } from 'react';
import type { DesignSystemName, PrimitivesRegistry } from './primitives.types.js';
import { tailwindRegistry } from './impl/tailwind.js';

/* ---------- Registry resolution ---------- */

const resolveRegistry = (_ds: DesignSystemName): PrimitivesRegistry => tailwindRegistry;
const resolveDesignSystem = (_ds: DesignSystemName): DesignSystemName => 'tailwind';

/* ---------- Context ---------- */
type DesignSystemContextValue = {
  designSystem: DesignSystemName;
  registry: PrimitivesRegistry;
};

const DesignSystemContext = createContext<DesignSystemContextValue | null>(null);

export const useDesignSystem = (): DesignSystemContextValue => {
  const ctx = useContext(DesignSystemContext);
  if (ctx) return ctx;
  // Tailwind-only default fallback.
  return { designSystem: 'tailwind', registry: tailwindRegistry };
};

/* ---------- Provider ---------- */
export type DesignSystemProviderProps = {
  designSystem: DesignSystemName;
  children: React.ReactNode;
};

export function DesignSystemProvider({ designSystem, children }: DesignSystemProviderProps) {
  const value = useMemo<DesignSystemContextValue>(
    () => ({ designSystem: resolveDesignSystem(designSystem), registry: resolveRegistry(designSystem) }),
    [designSystem],
  );

  return (
    <DesignSystemContext.Provider value={value}>
      {children}
    </DesignSystemContext.Provider>
  );
}

/* ---------- Boundary ---------- */
/**
 * Tailwind-only boundary; passthrough wrapper.
 */
export function DesignSystemBoundary({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}
